import { Button } from "components/Button/Button";
import { SchemaFormFragment } from "components/Form/components/SchemaForm/fragments/SchemaFormFragment";
import { Icon } from "components/Icon/Icon";
import { Popover } from "components/Popover/Popover";
import { FeConfigType } from "config/config";
import { getRecordClass } from "config/resourceUtils";
import { actions as resourcesActions } from "containers/Resources/Resources.reducer";
import { AttributeValueSchemaDTO } from "containers/Resources/screens/ResourcesSingle/AttributeValueSchema";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { RouterContext } from "utils/RouterManager/RouterManager";

/**
 * Synthetic schema-form schema used to render + edit a record's top-level
 * `description` (a plain markdown string) with the SAME datatype-aware markdown
 * editor the custom-object markdown ATTRIBUTE cells use (scope (i) slice (c)).
 * Description is not a schema attribute, so there is no per-attribute schema to
 * reuse — a `form-markdown` string schema gives an identical read-only preview
 * (clipped via ExpandableCell) and an identical pop-out Monaco/markdown editor.
 */
const DESCRIPTION_MARKDOWN_SCHEMA = { element: "form-markdown", type: "string" };

/**
 * Inline-edit normal RECORD fields (name / description) in list views
 * (operator-task-fe-inline-edit-list-views, scope (ii)). This is the sibling of
 * the custom-object ATTRIBUTE inline editors in AttributeValueSchema.tsx (scope
 * (i)): same hover-revealed-pencil + anchored-popover affordance and the same
 * single-record update saga, but the mutation targets a TOP-LEVEL column
 * (`name`, `description`) rather than a `value.<attr>` sub-key.
 *
 * Why the pencil (not click-to-edit) for the name cell: the name cell is the
 * row's primary navigation affordance (a NavLink to the record). A click-to-edit
 * model would hijack navigation, so the name text stays the link and a separate
 * pencil (revealed on hover, and kept persistently visible on touch devices via
 * an `@media (hover: none)` rule in ResourcesSingle.scss) opens the editor.
 */

/**
 * Whether a record's top-level fields may be inline-edited from a list cell.
 * Mirrors the writability gate the attribute editors use (getAttributeEditMode):
 * a record that is read-only (record / project / bundle) or trashed shows no
 * edit affordance.
 */
export const isRecordFieldEditable = (record: any): boolean =>
    !!record?.id &&
    !record?.is_readonly &&
    !record?.is_project_readonly &&
    !record?.is_bundle_readonly &&
    !record?.deleted_at;

/**
 * Container-level git write-lock gate. While a bundle/project/workspace holds a
 * git write lock (its `gitLockData` entry is in state "WRITE_START"), records in
 * that container must not be edited — the same lock the full-record ResourcesSingle
 * editor honours (see its `isGitLocked`). `gitLockData` comes from RouterContext
 * and is keyed by the container id. A record with no resolvable container is never
 * git-locked.
 *
 * Reads the top-level container-id columns (project_id / bundle_id / workspace_id),
 * falling back to the `*_id_<container>` relation objects some list selections
 * carry instead of the scalar.
 */
export const isRecordGitLocked = (record: any, gitLockData: any): boolean => {
    if (!gitLockData) {
        return false;
    }
    const containerId =
        record?.project_id ??
        record?.bundle_id ??
        record?.workspace_id ??
        record?.project_id_project?.id ??
        record?.bundle_id_bundle?.id ??
        record?.workspace_id_workspace?.id;
    if (!containerId) {
        return false;
    }
    return gitLockData?.[containerId]?.state === "WRITE_START";
};

/**
 * Full list-cell writability gate for NON-custom-object resource types: a record
 * is inline-editable only when it passes the read-only/trash gate
 * (isRecordFieldEditable) AND its container is not git-write-locked
 * (isRecordGitLocked). Custom objects keep the plain isRecordFieldEditable gate —
 * their inline editors shipped before git-lock gating and their list selections do
 * not carry the container id — so this stricter gate is applied where the container
 * relations are fetched (the non-CO resource-class list path).
 */
export const isRecordFieldEditableWithGitLock = (record: any, gitLockData: any): boolean =>
    isRecordFieldEditable(record) && !isRecordGitLocked(record, gitLockData);

/**
 * Whether a row's top-level `name`/`description` may be inline-edited from the
 * SHARED resource-list columns (commonColumns.name / commonColumns.description —
 * advanced search + resource listings), where every record class is rendered
 * through one column set (operator-task-fe-inline-edit-list-views-followups,
 * item 1, second surface).
 *
 * Only RESOURCE-class records (flows, connectors, settings, custom objects, …)
 * have a top-level-field update saga; ACTIVITY (executions) and RELATIONSHIP rows
 * do not, so they never get an edit affordance. On top of the class check, the
 * same read-only/trash + container git-write-lock gate the dashboard record_list
 * widget uses applies (isRecordFieldEditableWithGitLock) — the list query fetches
 * the writability + container-id fields it reads (see record.tsx
 * `baseResourceBody`). Name and description share this one gate.
 */
export const isRecordSharedListInlineEditable = (
    record: any,
    resourceName: string | undefined,
    gitLockData: any,
): boolean =>
    !!resourceName &&
    getRecordClass(resourceName as FeConfigType) === "resource" &&
    isRecordFieldEditableWithGitLock(record, gitLockData);

/**
 * The shared-list inline-editability gate, as consumed by the name column
 * (RecordNameInlineEditPencil). Retained as a named alias so item-1 (first
 * name-column surface) call sites and tests read intent-first; delegates to the
 * name/description-shared predicate isRecordSharedListInlineEditable.
 */
export const isRecordNameInlineEditable = isRecordSharedListInlineEditable;

/**
 * Dispatch a single top-level-field update via the existing single-record update
 * saga (START_UPDATE_RESOURCE). Unlike dispatchAttributeUpdate (which resends the
 * whole `value` blob with one attribute changed), this sends just the changed
 * column — `name`/`description` are already valid update variables on the base
 * `record` GraphQL config, so no saga/mutation change is needed. Resolves on
 * success, rejects on failure (the saga surfaces the error via its own toast).
 */
const dispatchTopLevelUpdate = (
    dispatch: any,
    resourceName: string,
    record: any,
    field: string,
    newValue: any
) =>
    new Promise((resolve, reject) => {
        dispatch({
            type: resourcesActions.START_UPDATE_RESOURCE,
            promise: { resolve, reject },
            resourceName,
            variables: {
                id: record?.id,
                [field]: newValue,
                // Optimistic-concurrency guard: reject (→ reload/overwrite dialog)
                // instead of silently clobbering a concurrent change.
                expected_version: record?.record_version,
            },
        });
    });

type RecordFieldInlineEditPencilProps = {
    record: any;
    field: "name" | "description";
    value: string | null | undefined;
    /** GraphQL resource name of the row's record type (e.g. "custom_object"). */
    resourceName?: string;
    /** Reload the list after a successful edit so later edits read fresh data. */
    refresh?: () => void;
};

/**
 * The pencil edit affordance + anchored popover editor for a top-level record
 * field. Renders ONLY the pencil (+ popover) — place it next to the cell's
 * existing read-only content (e.g. the name NavLink) inside a
 * `.inlineEditPopoverCell` wrapper so the shared hover-reveal CSS applies.
 *
 * Currently a single-line text editor (used for `name`). A colliding rename is
 * rejected by the backend name-uniqueness check → the saga toasts and the list
 * is not refreshed, so the cell keeps showing the old (still-current) name.
 * `description` (multi-line markdown, via a pop-out code editor) will extend this
 * in a later slice.
 */
export const RecordFieldInlineEditPencil = ({
    record,
    field,
    value,
    resourceName = "custom_object",
    refresh,
}: RecordFieldInlineEditPencilProps) => {
    const dispatch = useDispatch();
    const [isOpen, setIsOpen] = useState(false);
    const [localValue, setLocalValue] = useState<string>(value ?? "");
    const [isSaving, setIsSaving] = useState(false);
    const inputRef = useRef<HTMLInputElement | null>(null);

    // Re-sync the editor value when the underlying row value changes externally
    // (list refresh / poll) while the editor is closed and no save is in flight.
    useEffect(() => {
        if (!isOpen && !isSaving) {
            setLocalValue(value ?? "");
        }
    }, [value, isOpen, isSaving]);

    const openEditor = useCallback(() => {
        if (isSaving) {
            return;
        }
        setLocalValue(value ?? "");
        setIsOpen(true);
        // Focus + select the input once the popover has mounted.
        window.setTimeout(() => {
            inputRef.current?.focus();
            inputRef.current?.select();
        }, 0);
    }, [isSaving, value]);

    const cancel = useCallback(() => {
        setLocalValue(value ?? "");
        setIsOpen(false);
    }, [value]);

    const save = useCallback(() => {
        const trimmed = (localValue ?? "").trim();
        setIsOpen(false);
        // `name` is required and non-empty; block a whitespace-only rename and
        // revert to the current value.
        if (field === "name" && trimmed === "") {
            setLocalValue(value ?? "");
            return;
        }
        const newValue = field === "name" ? trimmed : localValue;
        if (newValue === (value ?? "")) {
            return;
        }
        setIsSaving(true);
        dispatchTopLevelUpdate(dispatch, resourceName, record, field, newValue)
            .then(() => {
                setIsSaving(false);
                refresh?.();
            })
            .catch(() => {
                // Revert the optimistic value; the saga already toasts the error
                // (e.g. a name-uniqueness collision on rename).
                setLocalValue(value ?? "");
                setIsSaving(false);
            });
    }, [dispatch, resourceName, record, field, value, localValue, refresh]);

    return (
        <Popover
            trigger={<Icon byName="pencilOutline" className="inlineEditPopoverCell__pencil" />}
            toggleClassName="inlineEditPopoverCell__trigger"
            isOpen={isOpen}
            actionOnOpen={openEditor}
            closeOnInsideClick={false}
            closeOnOutsideClick={false}
            closeHandler={cancel}
            onBlur={cancel}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            contentClassName="inlineEditPopover"
        >
            <div
                className="inlineEditPopover__body"
                onKeyDown={(event) => {
                    if (event.key === "Escape") {
                        event.preventDefault();
                        cancel();
                    } else if (event.key === "Enter") {
                        event.preventDefault();
                        save();
                    }
                }}
            >
                <input
                    ref={inputRef}
                    type="text"
                    className="inlineEditPopover__input"
                    value={localValue}
                    onChange={(event) => setLocalValue(event.target.value)}
                />
            </div>
            <div className="inlineEditPopover__footer">
                <Button variation="secondary" size="small" label="Cancel" onClick={cancel} />
                <Button
                    variation="primary"
                    size="small"
                    label="Save"
                    loading={isSaving}
                    onClick={save}
                />
            </div>
        </Popover>
    );
};

/**
 * List-cell inline name-edit pencil for the SHARED resource-list name column
 * (commonColumns.name — advanced search + resource listings). Self-contained: it
 * reads the container git-write-lock state from RouterContext itself, so the plain
 * column `dto` (not a React component, so it cannot read context) need only render
 * this next to the name link. Renders the name pencil only for editable
 * resource-class rows (isRecordNameInlineEditable); otherwise renders nothing, so a
 * read-only / git-locked / activity (execution) row keeps just its navigation link.
 * `refresh` is the table's row-refresh callback (the dto's afterButtonAction arg).
 */
export const RecordNameInlineEditPencil = ({
    record,
    resourceName,
    refresh,
}: {
    record: any;
    resourceName?: string;
    refresh?: () => void;
}) => {
    const gitLockData = useContext(RouterContext)?.gitLockData;
    if (!isRecordNameInlineEditable(record, resourceName, gitLockData)) {
        return null;
    }
    return (
        <RecordFieldInlineEditPencil
            record={record}
            field="name"
            value={record?.name}
            resourceName={resourceName}
            refresh={refresh}
        />
    );
};

type RecordDescriptionInlineEditProps = {
    record: any;
    value: string | null | undefined;
    /** GraphQL resource name of the row's record type (e.g. "custom_object"). */
    resourceName?: string;
    /** Reload the list after a successful edit so later edits read fresh data. */
    refresh?: () => void;
    /**
     * Optional writability override. When omitted, the component uses the plain
     * read-only/trash gate (isRecordFieldEditable) — the custom-object path. Non-CO
     * resource-class callers pass the git-lock-aware decision
     * (isRecordFieldEditableWithGitLock) so a git-write-locked container's rows show
     * only the read-only preview.
     */
    editable?: boolean;
};

/**
 * Inline-edit a record's top-level `description` from a list cell
 * (operator-task-fe-inline-edit-list-views, scope (ii), slice 2). Description is
 * a multi-line markdown string, so — unlike the single-line `name` editor above —
 * it uses a POP-OUT markdown editor: the cell shows a clipped read-only markdown
 * preview plus a pencil that opens an anchored popover hosting the same
 * datatype-aware markdown editor as the custom-object markdown attribute cells
 * (scope (i) slice (c)).
 *
 * Save model mirrors PopoverEditAttributeValueSchemaDTO exactly, because the
 * Monaco/markdown editor blurs on its own (Save mousedown, toolbar clicks,
 * autocomplete) and would commit unpredictably under save-on-blur:
 *  - editing stays local until the user clicks Save (or Ctrl/Cmd+Enter);
 *  - the latest edited value is held in a ref so the Save handler reads the
 *    freshest value even when the editor flushes via an onBlur that fires in the
 *    same click as Save;
 *  - a `savingInitiatedRef` (set on the Save button mousedown, before the editor
 *    blurs) lets cancel() swallow the one spurious MUIPopover onClose the markdown
 *    editor's blur triggers, so the edit is not discarded before save() commits.
 *
 * Commit targets the TOP-LEVEL `description` column via dispatchTopLevelUpdate
 * (not the attribute-value blob): optimistic value, revert on failure (the saga
 * toasts the error), refresh the list on success. Non-editable rows (read-only /
 * trashed, per isRecordFieldEditable) render only the read-only preview.
 */
export const RecordDescriptionInlineEdit = ({
    record,
    value,
    resourceName = "custom_object",
    refresh,
    editable: editableProp,
}: RecordDescriptionInlineEditProps) => {
    const dispatch = useDispatch();
    const contextAjv = useContext(RouterContext)?.ajv;
    const [isOpen, setIsOpen] = useState(false);
    const [localValue, setLocalValue] = useState<string>(value ?? "");
    const [isSaving, setIsSaving] = useState(false);
    const latestValueRef = useRef<string>(value ?? "");
    const savingInitiatedRef = useRef(false);
    const editable = editableProp ?? isRecordFieldEditable(record);

    // Re-sync the display value when the underlying row value changes externally
    // (list refresh / poll) while the editor is closed and no save is in flight.
    useEffect(() => {
        if (!isOpen && !isSaving) {
            setLocalValue(value ?? "");
            latestValueRef.current = value ?? "";
        }
    }, [value, isOpen, isSaving]);

    const openEditor = useCallback(() => {
        if (isSaving) {
            return;
        }
        setLocalValue(value ?? "");
        latestValueRef.current = value ?? "";
        savingInitiatedRef.current = false;
        setIsOpen(true);
    }, [isSaving, value]);

    // The editor's setData: keep the ref and state in sync as the user edits.
    const handleChange = useCallback((newValue: any) => {
        latestValueRef.current = newValue ?? "";
        setLocalValue(newValue ?? "");
    }, []);

    const cancel = useCallback(() => {
        if (savingInitiatedRef.current) {
            // A Save click is in flight (its mousedown set this flag). The markdown
            // editor blurs on that click and triggers MUIPopover's onClose -> this
            // handler; swallow that single spurious close so it does not discard the
            // edit before save() commits it.
            savingInitiatedRef.current = false;
            return;
        }
        setLocalValue(value ?? "");
        latestValueRef.current = value ?? "";
        setIsOpen(false);
    }, [value]);

    const save = useCallback(() => {
        savingInitiatedRef.current = false;
        const newValue = latestValueRef.current ?? "";
        setIsOpen(false);
        if (newValue === (value ?? "")) {
            return;
        }
        setLocalValue(newValue);
        setIsSaving(true);
        dispatchTopLevelUpdate(dispatch, resourceName, record, "description", newValue)
            .then(() => {
                setIsSaving(false);
                refresh?.();
            })
            .catch(() => {
                // Revert the optimistic value; the saga already toasts the error.
                setLocalValue(value ?? "");
                latestValueRef.current = value ?? "";
                setIsSaving(false);
            });
    }, [dispatch, resourceName, record, value, refresh]);

    const preview = (
        <div className="attributeValueSchemaContainer inlineEditPopoverCell__preview">
            <AttributeValueSchemaDTO
                value={localValue}
                schema={DESCRIPTION_MARKDOWN_SCHEMA}
                ajv={contextAjv}
            />
        </div>
    );

    if (!editable) {
        return <div className="inlineEditPopoverCell">{preview}</div>;
    }

    return (
        <div className="inlineEditPopoverCell">
            {preview}
            <Popover
                trigger={<Icon byName="pencilOutline" className="inlineEditPopoverCell__pencil" />}
                toggleClassName="inlineEditPopoverCell__trigger"
                isOpen={isOpen}
                actionOnOpen={openEditor}
                closeOnInsideClick={false}
                // Do NOT use the Popover's custom document-level useOutsideClick
                // listener: it closes this controlled popover on stray document
                // clicks before the lazily-loaded Monaco/markdown editor mounts.
                // Outside-click and Escape still close via MUIPopover's own onClose
                // (backdrop + Escape) -> onBlur={cancel}. (Same rationale as
                // PopoverEditAttributeValueSchemaDTO.)
                closeOnOutsideClick={false}
                closeHandler={cancel}
                onBlur={cancel}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                contentClassName="inlineEditPopover"
            >
                <div
                    className="inlineEditPopover__body attributeValueSchemaContainer isInlineEditing"
                    onKeyDown={(event) => {
                        if (event.key === "Escape") {
                            event.preventDefault();
                            cancel();
                        } else if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
                            event.preventDefault();
                            save();
                        }
                    }}
                >
                    <SchemaFormFragment
                        isRoot={true}
                        isReadOnly={false}
                        isRecordDisabled={false}
                        hideActionButtons={true}
                        schema={DESCRIPTION_MARKDOWN_SCHEMA}
                        schemaPath={[]}
                        formData={localValue}
                        dataPath={[]}
                        setData={handleChange}
                        showLabel={false}
                        ajv={contextAjv}
                        additionalCodeEditorRootProps={{
                            initialHeight: 200,
                            minHeight: 160,
                        }}
                    />
                </div>
                <div className="inlineEditPopover__footer">
                    <Button variation="secondary" size="small" label="Cancel" onClick={cancel} />
                    <span
                        // Layout-neutral wrapper (Button does not forward onMouseDown).
                        // mousedown fires before the editor blur, so this marks the
                        // save-in-flight before the markdown editor's blur can trigger
                        // MUIPopover onClose -> cancel().
                        style={{ display: "contents" }}
                        onMouseDown={() => {
                            savingInitiatedRef.current = true;
                        }}
                    >
                        <Button
                            variation="primary"
                            size="small"
                            label="Save"
                            loading={isSaving}
                            onClick={save}
                        />
                    </span>
                </div>
            </Popover>
        </div>
    );
};

/**
 * Self-contained list-cell Description editor for the SHARED resource-list
 * Description column (commonColumns.description — advanced search + resource
 * listings), where a plain column `dto` (not a React component, so it cannot read
 * context) renders one cell per row
 * (operator-task-fe-inline-edit-list-views-followups, item 1, second surface —
 * description sibling of RecordNameInlineEditPencil). Like that name pencil it
 * reads the container git-write-lock state from RouterContext itself, then passes
 * the git-lock-aware editable decision (isRecordFieldEditableWithGitLock) down to
 * RecordDescriptionInlineEdit:
 *  - a RESOURCE-class row (flow / connector / setting / custom object / …) that is
 *    writable and not git-write-locked gets the editable pencil + pop-out markdown
 *    editor;
 *  - a read-only / trashed / git-locked row, or a non-resource-class row (defensive;
 *    this column is only added to resource-class lists), shows just the clipped
 *    read-only markdown preview.
 * `refresh` is the table's row-refresh callback (the dto's afterButtonAction arg).
 */
export const RecordDescriptionInlineEditCell = ({
    record,
    resourceName,
    refresh,
}: {
    record: any;
    resourceName?: string;
    refresh?: () => void;
}) => {
    const gitLockData = useContext(RouterContext)?.gitLockData;
    const editable = isRecordSharedListInlineEditable(record, resourceName, gitLockData);
    return (
        <RecordDescriptionInlineEdit
            record={record}
            value={record?.description}
            resourceName={resourceName}
            refresh={refresh}
            editable={editable}
        />
    );
};
